import { ConcernView } from "@/components/explore/ConcernView";
import { ExploreContext, ZoneData } from "@/components/explore/ExploreContext";
import type { TableCellFormatter } from "@/components/explore/ExploreTable";
import { TableView } from "@/components/explore/ExploreTable";
import { FlightContributionsModal } from "@/components/explore/FlightContributionsModal";
import { HealthCheckBadge } from "@/components/explore/HealthCheckBadge";
import type { TooltipFormatter } from "@/components/explore/Heatmap";
import { HeatMapView } from "@/components/explore/Heatmap";
import { gateLoungePaxTooltipFormatter } from "@/lib/tooltipFormatters";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getZoneData } from "@/queries/report";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import { ExploreConfig } from "@/types/explore";
import _ from "lodash";
import { FunctionComponent, useMemo, useState } from "react";

function getTimeFromIndex(
  index: number,
  intervalSize: number = 10,
  startHour = 0,
  endHour = 24
): string {
  // Determine how many intervals per hour.
  const intervalsPerHour = 60 / intervalSize;

  // Translate the index to the corresponding hour and minute.
  const hour =
    (Math.floor(index / intervalsPerHour) + startHour) % 24;
  const minute = (index % intervalsPerHour) * intervalSize;

  // Build a time string and return it.
  return `${hour}:${minute.toString().padStart(2, "0")}`;
}

export function trimZones(
  zones: ZoneData[],
  startHour: number,
  endHour: number,
  intervalSize: number = 10
): { zones: ZoneData[]; times: string[] } {
  const times: string[] = [];

  if (zones.length > 0 && zones[0].values.length > 0) {
    _.forEach(zones[0].values, (_, index) => {
      times.push(getTimeFromIndex(index, intervalSize, startHour, endHour));
    });
  }

  return { zones, times };
}

export const ExploreView: FunctionComponent<{
  config: ExploreConfig;
  heatMapHeight?: number;
  showHealthCheck?: boolean;
  customTooltipFormatter?: TooltipFormatter;
  customCellFormatter?: TableCellFormatter;
}> = ({
  heatMapHeight = 950,
  config,
  showHealthCheck = false,
  customTooltipFormatter,
  customCellFormatter,
}) => {
  const [exploreMode, setExploreMode] = useState("heatmap");
  const [contributionsModalOpen, setContributionsModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{
    zoneId: number;
    interval: number;
    zoneName: string;
    timeLabel: string;
    zoneIds?: number[];
  } | null>(null);

  const handleCellClick = (
    zoneId: number,
    interval: number,
    zoneName: string,
    timeLabel: string,
    zoneIds?: number[]
  ) => {
    setSelectedCell({ zoneId, interval, zoneName, timeLabel, zoneIds });
    setContributionsModalOpen(true);
  };

  const error = useMemo(() => {
    return null;
  }, [config]);

  const { reportId } = useReportContext();
  const {
    data: zoneData,
    isLoading,
    isError,
    isSuccess,
  } = getZoneData(config, reportId, !error);
  const { airport_config } = useSessionData();

  if (error) {
    return <div className={"error-msg"}>{error}</div>;
  }

  if (isLoading) return <div>Please wait while we load your data.</div>;

  if (isError || !isSuccess) {
    return (
      <div className={"error-msg"}>
        Something went wrong when fetching the data you requested
      </div>
    );
  }

  if (!zoneData.zones || zoneData.zones.length == 0) {
    return (
      <div className={"error-msg"}>Your request did not return any results</div>
    );
  }

  const { zones, times } = trimZones(
    zoneData.zones,
    airport_config.start_hour,
    airport_config.end_hour,
    airport_config.interval_size
  );

  const heatmapTooltipFormatter: TooltipFormatter | undefined =
    customTooltipFormatter ??
    (config.type === "gate" && config.measure === "pax"
      ? gateLoungePaxTooltipFormatter
      : undefined);

  return (
    <div className={"w-full"}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <span className={"text-sm mr-2"}>View as:</span>
          <Tabs
            value={exploreMode}
            onValueChange={(value) => setExploreMode(value)}
          >
            <TabsList>
              <TabsTrigger value="heatmap">Heatmap</TabsTrigger>
              <TabsTrigger value="table">Table</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {showHealthCheck && <HealthCheckBadge zones={zones} times={times} />}
      </div>
      <ExploreContext.Provider
        value={{
          zones,
          times,
          concerns: zoneData.concerns,
          zoneType: config.type,
        }}
      >
        <div className={"dark:bg-slate-900 py-2 mt-4 overflow-hidden"}>
          {exploreMode === "heatmap" && (
            <HeatMapView
              measure={config.measure}
              height={heatMapHeight}
              customTooltipFormatter={heatmapTooltipFormatter}
              onCellClick={handleCellClick}
            />
          )}
          {exploreMode === "table" && (
            <TableView
              customCellFormatter={customCellFormatter}
              onCellClick={handleCellClick}
            />
          )}
        </div>
        {zoneData.concerns && zoneData.concerns.length > 0 && <ConcernView />}
      </ExploreContext.Provider>

      <FlightContributionsModal
        open={contributionsModalOpen}
        onOpenChange={setContributionsModalOpen}
        zoneId={selectedCell?.zoneId ?? null}
        interval={selectedCell?.interval ?? null}
        zoneName={selectedCell?.zoneName}
        timeLabel={selectedCell?.timeLabel}
        measure={config.measure}
        zoneIds={selectedCell?.zoneIds}
        zoneType={config.type}
      />
    </div>
  );
};
